<script setup lang="ts">
import type { PropType, Ref, WritableComputedRef } from 'vue'
import NumberStepperField from '@/components/NumberStepperField.vue'
import ResourceSelectField from '@/features/virtual-resource/components/ResourceSelectField.vue'
import type { SettingsImageOptionDraft } from '@/features/workspace/settings/useSettingsImageOptions'

type NumberModelRef = Ref<number> | WritableComputedRef<number>

defineProps({
  imageOptionButtons: { type: Array as PropType<SettingsImageOptionDraft[]>, required: true },
  graphicBaseFolderPath: { type: String, required: true },
  imageOptionMaxCountRef: { type: Object as PropType<NumberModelRef>, required: true },
  imageOptionSelectedIndexRef: { type: Object as PropType<Ref<number>>, required: true },
  imageOptionHoverRef: { type: Object as PropType<Ref<boolean>>, required: true },
  portraitLayout: { type: Boolean, default: false },
  selectedImageOption: { type: Object as PropType<SettingsImageOptionDraft | null>, default: null },
  isReservedImageOption: { type: Boolean, default: false },
  selectedImageOptionNoneUrl: { type: String, required: true },
  selectedImageOptionMoveUrl: { type: String, required: true },
  imageOptionTestUrl: { type: String, required: true },
  showImageOptionButtonTest: { type: Boolean, default: false },
  selectImageOption: { type: Function as PropType<(index: number) => void>, required: true },
  formatImageOptionLabel: {
    type: Function as PropType<(index: number, item: SettingsImageOptionDraft) => string>,
    required: true,
  },
  updateSelectedImageOptionNonePic: { type: Function as PropType<(value: string) => void>, required: true },
  updateSelectedImageOptionMovePic: { type: Function as PropType<(value: string) => void>, required: true },
})
</script>

<template>
  <div v-if="portraitLayout" class="settings-page settings-portrait-page image-option-page-portrait">
    <section v-if="showImageOptionButtonTest" class="setting-card settings-portrait-preview image-option-preview-card">
      <div class="image-option-test-shell image-option-test-shell--portrait">
        <button
          type="button"
          class="image-option-test-button"
          @mouseenter="imageOptionHoverRef.value = true"
          @mouseleave="imageOptionHoverRef.value = false"
        >
          <img :src="imageOptionTestUrl" alt="按钮测试预览" draggable="false" />
        </button>
      </div>
    </section>

    <div class="settings-portrait-props">
      <section class="setting-card image-option-list-card">
        <div class="card-title">图片选项列表</div>

        <label class="field-block">
          <span class="field-label">最大值</span>
          <NumberStepperField v-model="imageOptionMaxCountRef.value" class="cell-input sm" :min="1" />
        </label>

        <div v-if="imageOptionButtons.length" class="picker-scroll-list">
          <button
            v-for="(item, index) in imageOptionButtons"
            :key="`image-option-portrait-${index}`"
            type="button"
            class="picker-scroll-item"
            :class="{ 'picker-scroll-item--active': index === imageOptionSelectedIndexRef.value }"
            @click="selectImageOption(index)"
          >
            {{ formatImageOptionLabel(index, item) }}
          </button>
        </div>
        <div v-else class="placeholder-card image-option-empty">当前没有图片选项</div>
      </section>

      <section class="setting-card image-option-detail-card">
        <template v-if="selectedImageOption">
          <div class="card-title">选中项设置</div>

          <label class="field-block">
            <span class="field-label">名称</span>
            <input v-model="selectedImageOption.Name" class="cell-input wide" :disabled="isReservedImageOption" />
          </label>

          <label class="field-block">
            <span class="field-label">未点击图片</span>
            <ResourceSelectField
              :model-value="selectedImageOption.NoneImage.name"
              category="graphic"
              :base-folder-path="graphicBaseFolderPath"
              :disabled="isReservedImageOption"
              display-mode="full"
              store-mode="relative"
              picker-title="选择未点击图片"
              @update:model-value="updateSelectedImageOptionNonePic"
            />
          </label>

          <label class="field-block">
            <span class="field-label">点击图片</span>
            <ResourceSelectField
              :model-value="selectedImageOption.MoveImage.name"
              category="graphic"
              :base-folder-path="graphicBaseFolderPath"
              :disabled="isReservedImageOption"
              display-mode="full"
              store-mode="relative"
              picker-title="选择点击图片"
              @update:model-value="updateSelectedImageOptionMovePic"
            />
          </label>
        </template>

        <div v-else class="placeholder-card image-option-empty">请先设置最大值并选择左侧的一项</div>
      </section>
    </div>
  </div>

  <div v-else class="settings-page image-option-page">
    <div class="image-option-grid">
      <section class="setting-card image-option-list-card">
        <div class="card-title">图片选项列表</div>

        <label class="field-block">
          <span class="field-label">最大值</span>
          <NumberStepperField v-model="imageOptionMaxCountRef.value" class="cell-input sm" :min="1" />
        </label>

        <div v-if="imageOptionButtons.length" class="picker-scroll-list">
          <button
            v-for="(item, index) in imageOptionButtons"
            :key="`image-option-${index}`"
            type="button"
            class="picker-scroll-item"
            :class="{ 'picker-scroll-item--active': index === imageOptionSelectedIndexRef.value }"
            @click="selectImageOption(index)"
          >
            {{ formatImageOptionLabel(index, item) }}
          </button>
        </div>
        <div v-else class="placeholder-card image-option-empty">当前没有图片选项</div>
      </section>

      <section class="setting-card image-option-detail-card">
        <template v-if="selectedImageOption">
          <div class="card-title">选中项设置</div>

          <label class="field-block">
            <span class="field-label">名称</span>
            <input v-model="selectedImageOption.Name" class="cell-input wide" :disabled="isReservedImageOption" />
          </label>

          <label class="field-block">
            <span class="field-label">未点击图片</span>
            <ResourceSelectField
              :model-value="selectedImageOption.NoneImage.name"
              category="graphic"
              :base-folder-path="graphicBaseFolderPath"
              :disabled="isReservedImageOption"
              display-mode="full"
              store-mode="relative"
              picker-title="选择未点击图片"
              @update:model-value="updateSelectedImageOptionNonePic"
            />
          </label>

          <label class="field-block">
            <span class="field-label">点击图片</span>
            <ResourceSelectField
              :model-value="selectedImageOption.MoveImage.name"
              category="graphic"
              :base-folder-path="graphicBaseFolderPath"
              :disabled="isReservedImageOption"
              display-mode="full"
              store-mode="relative"
              picker-title="选择点击图片"
              @update:model-value="updateSelectedImageOptionMovePic"
            />
          </label>

          <div v-if="showImageOptionButtonTest" class="image-option-test-card">
            <div class="field-label">按钮测试</div>
            <div class="image-option-test-shell">
              <button
                type="button"
                class="image-option-test-button"
                @mouseenter="imageOptionHoverRef.value = true"
                @mouseleave="imageOptionHoverRef.value = false"
              >
                <img :src="imageOptionTestUrl" alt="按钮测试预览" draggable="false" />
              </button>
            </div>
          </div>
        </template>

        <div v-else class="placeholder-card image-option-empty">请先设置最大值并选择左侧的一项</div>
      </section>
    </div>
  </div>
</template>

<style scoped>
@import './shared.css';

.settings-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  padding-top: 4px;
  box-sizing: border-box;
}

.image-option-grid {
  display: grid;
  grid-template-columns: var(--settings-layout-left-width) minmax(0, 1fr);
  gap: 8px;
  height: 100%;
  min-height: 0;
  align-items: stretch;
  align-content: stretch;
}

.image-option-list-card,
.image-option-detail-card {
  height: 100%;
  min-height: 0;
}

.image-option-empty {
  min-height: 120px;
}

.image-option-test-card {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.image-option-test-shell {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 140px;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-raised);
  overflow: auto;
}

.image-option-test-shell--portrait {
  flex: 1;
  min-height: 0;
  border: 0;
}

.image-option-test-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 96px;
  min-height: 40px;
  padding: 0;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-panel);
  color: var(--theme-text-heading);
}

.image-option-test-button img {
  display: block;
  max-width: 100%;
  max-height: 180px;
  object-fit: contain;
}

.placeholder-card {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-panel);
  color: var(--theme-text-muted);
  font-size: 12px;
}

@media (max-width: 980px) and (orientation: portrait) {
  .image-option-grid {
    grid-template-columns: 1fr;
  }
}
</style>
